import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import AuthContext from "../Context/AuthProvider";
import { getPatients } from "../API/PatientsApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid2,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  PeopleOutline,
  PersonAdd,
  Male,
  Female,
  LocationOn,
  Cake,
  Visibility,
  Close,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { capitalizeFirstLetter, formatAge } from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import { exportPDF } from "../Utility/PdfConverterForReport";
import Chart from "chart.js/auto";

// ─── Chart.js global instances ───────────────────────────────────────────────
let chartInstances = {};

// ─── Colors ──────────────────────────────────────────────────────────────────
const C = {
  teal:   "#1D9E75",  tealL:   "#E1F5EE",
  coral:  "#D85A30",  coralL:  "#FAECE7",
  blue:   "#378ADD",  blueL:   "#E6F1FB",
  amber:  "#BA7517",  amberL:  "#FAEEDA",
  purple: "#7F77DD",  purpleL: "#EEEDFE",
  green:  "#639922",  greenL:  "#EAF3DE",
  pink:   "#D4537E",  pinkL:   "#FBEAF0",
};

// ─── CSV Download ─────────────────────────────────────────────────────────────
function downloadCSV(rows, columns, filename) {
  const header = columns.map((c) => c.headerName).join(",");
  const body = rows.map((row) =>
    columns.map((c) => `"${String(row[c.field] ?? "").replace(/"/g, '""')}"`).join(",")
  );
  const blob = new Blob([[header, ...body].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

// ─── Table Dialog — same as WorklistDashboard ─────────────────────────────────
function TableDialog({ open, onClose, title, rows, columns, csvFilename, auth }) {
  const handleDownloadPdf = () => {
    exportPDF(
      rows,
      columns,
      null,
      null,
      title,
      auth?.selectedFacility?.logo
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 0,
          px: 0,
        }}
      >
        <MenuBar showDownload={true} onDownload={handleDownloadPdf} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0, height: 460 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            density: "compact",
            pagination: { paginationModel: { pageSize: 20 } },
          }}
          pageSizeOptions={[10, 20, 50]}
          sx={{
            border: "none",
            height: 460,
            "& .MuiTablePagination-toolbar": { minHeight: "unset", alignItems: "center" },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { margin: 0 },
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color, bgColor, subtitle, onView }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: `1.5px solid ${bgColor}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          padding: "12px 14px !important",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28, height: 28, borderRadius: 1.5,
              background: bgColor, display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 15, color } })}
          </Box>
          <Typography
            variant="caption" color="text.secondary" fontWeight={500}
            sx={{ lineHeight: 1.2, fontSize: "0.68rem" }}
          >
            {label}
          </Typography>
        </Box>

        <Typography variant="h4" fontWeight={700} sx={{ color, lineHeight: 1, mt: 0.5 }}>
          {value}
        </Typography>

        {subtitle && (
          <Typography variant="caption" color="text.secondary"
            sx={{ fontSize: "0.62rem", lineHeight: 1.3 }}>
            {subtitle}
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />

        {onView && (
          <Box sx={{ mt: 1 }}>
            <Button
              size="small" variant="contained"
              startIcon={<Visibility sx={{ fontSize: "11px !important" }} />}
              onClick={onView}
              sx={{
                fontSize: "0.62rem", py: 0.25, px: 1,
                minHeight: 0, lineHeight: 1.4,
                background: color,
                "&:hover": { background: color, opacity: 0.88 },
                borderRadius: 1,
              }}
            >
              VIEW
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────
function SectionCard({ title, icon, color, children, onViewAll }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {icon && React.cloneElement(icon, { sx: { fontSize: 14, color } })}
            <Typography
              variant="body2" fontWeight={600} color="text.secondary"
              sx={{ textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}
            >
              {title}
            </Typography>
          </Box>
        }
        action={
          onViewAll && (
            <Button
              size="small"
              startIcon={<Visibility sx={{ fontSize: 12 }} />}
              onClick={onViewAll}
              sx={{ fontSize: "0.68rem", py: 0.2, color }}
            >
              View All
            </Button>
          )
        }
        sx={{ pb: 0, pt: 1.5, px: 2 }}
      />
      <CardContent sx={{ pt: 1, pb: "12px !important" }}>{children}</CardContent>
    </Card>
  );
}

// ─── Canvas wrapper ───────────────────────────────────────────────────────────
function ChartBox({ id, height = 180, label, minWidth }) {
  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Box sx={{ position: "relative", height, minWidth: minWidth || "100%" }}>
        <canvas id={id} role="img" aria-label={label}>{label}</canvas>
      </Box>
    </Box>
  );
}

// ─── Normalize location strings (bangalore == Bangalore == BANGALORE) ─────────
function normalizeLocation(str) {
  if (!str) return "Unknown";
  return str.trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Patient table columns ────────────────────────────────────────────────────
const patientCols = [
  { field: "mrn", headerName: "MRN", width: 130 },
  {
    field: "name", headerName: "Patient", width: 200,
    valueGetter: (_, r) =>
      `${capitalizeFirstLetter(r.firstName || "")} ${capitalizeFirstLetter(r.lastName || "")}`.trim(),
  },
  { field: "gender", headerName: "Gender", width: 100 },
  {
    field: "ageInYears", headerName: "Age", width: 100,
    valueGetter: (_, r) => formatAge(r.ageInYears),
  },
  { field: "mobileNo", headerName: "Phone", width: 140 },
  { field: "addressCity", headerName: "City", width: 140 },
  { field: "addressState", headerName: "State", width: 130 },
];

const locationCols = [
  { field: "id", headerName: "#", width: 60 },
  { field: "name", headerName: "Location", width: 220 },
  { field: "count", headerName: "Total Patients", width: 130 },
];

// ════════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
export default function PatientDashboard() {
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();

  const [patients, setPatients]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [locationView, setLocationView] = useState("city"); // "city" | "state"
  const [dialog, setDialog]           = useState({
    open: false, title: "", rows: [], columns: [], csv: "data.csv",
  });

  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    const result = await getPatients(auth);
    if (result.isSuccess) setPatients(result.data || []);
    else showSnackbar("Failed to load patient data", "error");
    setLoading(false);
  }, [auth]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  // ── Metrics ───────────────────────────────────────────────────────────────
  const m = useMemo(() => {
    const total = patients.length;

    // Gender split
    const maleP   = patients.filter((p) => p.gender?.toLowerCase() === "male");
    const femaleP = patients.filter((p) => p.gender?.toLowerCase() === "female");
    const otherP  = patients.filter(
      (p) => p.gender && !["male", "female"].includes(p.gender.toLowerCase())
    );

    // Newly added — last 30 days
    const thirtyDaysAgo = dayjs().subtract(30, "day");
    const newlyAdded = patients.filter((p) => {
      const d = p.createdDate || p.registrationDate || p.lastAccessedDate;
      return d ? dayjs(d).isAfter(thirtyDaysAgo) : false;
    });

    // City aggregation — normalized (case-insensitive, trimmed)
    const cityMap = {};
    patients.forEach((p) => {
      const city = normalizeLocation(p.addressCity);
      cityMap[city] = (cityMap[city] || 0) + 1;
    });



    const cityData      = Object.entries(cityMap).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const cityTableRows = Object.entries(cityMap)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count], i) => ({ id: i + 1, name, count }));

    // State aggregation — normalized
    const stateMap = {};
    patients.forEach((p) => {
      const state = normalizeLocation(p.addressState);
      stateMap[state] = (stateMap[state] || 0) + 1;
    });
    const stateData      = Object.entries(stateMap).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const stateTableRows = Object.entries(stateMap)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count], i) => ({ id: i + 1, name, count }));

    // Age range buckets
    const ageBuckets = {
      "0-10":   0,
      "11-20":  0,
      "21-30":  0,
      "31-40":  0,
      "41-50":  0,
      "51-60":  0,
      "61-70":  0,
      "71+":    0,
    };
    // Age bucket → patients map
const ageBucketPatients = {};
patients.forEach((p) => {
  const age = p.ageInYears;
  let bucket;
  if (age == null || isNaN(age)) return;
  if (age <= 10)      bucket = "0-10";
  else if (age <= 20) bucket = "11-20";
  else if (age <= 30) bucket = "21-30";
  else if (age <= 40) bucket = "31-40";
  else if (age <= 50) bucket = "41-50";
  else if (age <= 60) bucket = "51-60";
  else if (age <= 70) bucket = "61-70";
  else                bucket = "71+";
  if (!ageBucketPatients[bucket]) ageBucketPatients[bucket] = [];
  ageBucketPatients[bucket].push(p);
});
    
    patients.forEach((p) => {
      const age = p.ageInYears;
      if (age == null || isNaN(age)) return;
      if (age <= 10)       ageBuckets["0-10"]++;
      else if (age <= 20)  ageBuckets["11-20"]++;
      else if (age <= 30)  ageBuckets["21-30"]++;
      else if (age <= 40)  ageBuckets["31-40"]++;
      else if (age <= 50)  ageBuckets["41-50"]++;
      else if (age <= 60)  ageBuckets["51-60"]++;
      else if (age <= 70)  ageBuckets["61-70"]++;
      else                 ageBuckets["71+"]++;
    });

    const uniqueCities = Object.keys(cityMap).filter((c) => c !== "Unknown").length;
    const avgAge =
      patients.filter((p) => p.ageInYears).length > 0
        ? Math.round(
            patients.reduce((sum, p) => sum + (p.ageInYears || 0), 0) /
            patients.filter((p) => p.ageInYears).length
          )
        : "-";

    // Gender entries for doughnut
    const genderEntries = [
      ["Male",   maleP.length],
      ["Female", femaleP.length],
      ["Other",  otherP.length],
    ].filter(([, v]) => v > 0);

    return {
      total, maleP, femaleP, otherP, newlyAdded,
      cityData, cityTableRows,
      stateData, stateTableRows,
      ageBuckets,ageBucketPatients, uniqueCities, avgAge,
      genderEntries,
    };
  }, [patients]);

  const openPatientDlg = useCallback((title, rows, csv) =>
    setDialog({
      open: true, title,
      rows: rows.map((p, i) => ({ ...p, id: p.id || i })),
      columns: patientCols,
      csv,
    }), []);

  // Refs for click handlers inside chart effects
  const mRef = React.useRef(m);
  useEffect(() => { mRef.current = m; });
  const openPatientDlgRef = React.useRef(openPatientDlg);
  useEffect(() => { openPatientDlgRef.current = openPatientDlg; });
  const ageBucketPatientsRef = React.useRef(m.ageBucketPatients);
useEffect(() => { ageBucketPatientsRef.current = m.ageBucketPatients; });

  // ── Age Distribution Chart — traffic-colored like WorklistDashboard ────────
  useEffect(() => {
    if (loading) return;

    const destroy = (id) => {
      if (chartInstances[id]) { chartInstances[id].destroy(); delete chartInstances[id]; }
    };

    const aEl = document.getElementById("pd-age");
    const ageEntries = Object.entries(m.ageBuckets).filter(([, v]) => v > 0);
    if (aEl && ageEntries.length > 0) {
      destroy("age");
      const maxVal = Math.max(...ageEntries.map(([, v]) => v), 1);
      chartInstances["age"] = new Chart(aEl, {
        type: "bar",
        data: {
          labels: ageEntries.map(([k]) => k),
          datasets: [{
            label: "Patients",
            data: ageEntries.map(([, v]) => v),
            backgroundColor: ageEntries.map(([, v]) => {
              const r = v / maxVal;
              return r >= 0.75 ? "#D32F2F" + "CC" : r >= 0.4 ? "#F9A825" + "CC" : C.green + "CC";
            }),
            borderColor: ageEntries.map(([, v]) => {
              const r = v / maxVal;
              return r >= 0.75 ? "#D32F2F" : r >= 0.4 ? "#F9A825" : C.green;
            }),
            borderWidth: 1,
            borderRadius: 4,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const v = ctx.raw;
                  const r = v / maxVal;
                  const traffic = r >= 0.75 ? "🔴 High" : r >= 0.4 ? "🟡 Medium" : "🟢 Low";
                  return ` ${v} patients  ${traffic}`;
                },
              },
            },
          },
          scales: {
            x: {
              ticks: { font: { size: 10 }, maxRotation: 0 },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: { font: { size: 10 }, stepSize: 1 },
              grid: { color: "rgba(0,0,0,0.04)" },
            },
          },
          onClick: (evt, elements) => {
  if (!elements.length) return;
  const idx = elements[0].index;
  const lbl = ageEntries[idx][0];
  const rows = ageBucketPatientsRef.current?.[lbl] || [];
  if (rows.length > 0) {
    openPatientDlgRef.current(
      `Age Group ${lbl} — Patients`,
      rows,
      `age_${lbl}.csv`
    );
  }
},
        },
      });
    }

    return () => { destroy("age"); };
  }, [m.ageBuckets, loading]);

  // ── Location Chart (City or State) — switches on locationView ────────────
  useEffect(() => {
    if (loading) return;

    const destroy = (id) => {
      if (chartInstances[id]) { chartInstances[id].destroy(); delete chartInstances[id]; }
    };

    const lEl = document.getElementById("pd-location");
    const data = locationView === "city" ? m.cityData : m.stateData;
    if (lEl && data.length > 0) {
      destroy("location");
      chartInstances["location"] = new Chart(lEl, {
        type: "bar",
        data: {
          labels: data.map(([n]) => n.length > 22 ? n.slice(0, 20) + "…" : n),
          datasets: [{
            data: data.map(([, v]) => v),
            backgroundColor: [
              C.teal + "CC", C.purple + "CC", C.blue + "CC",
              C.coral + "CC", C.amber + "CC", C.pink + "CC",
            ],
            borderRadius: 4,
          }],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: {
              beginAtZero: true,
              ticks: { font: { size: 9 }, stepSize: 1 },
              grid: { color: "rgba(0,0,0,0.04)" },
            },
            y: { ticks: { font: { size: 10 } }, grid: { display: false } },
          },
        },
      });
    }

    return () => { destroy("location"); };
  }, [m.cityData, m.stateData, locationView, loading]);

  // ── Gender Doughnut — clickable ───────────────────────────────────────────
  useEffect(() => {
    if (loading) return;

    const destroy = (id) => {
      if (chartInstances[id]) { chartInstances[id].destroy(); delete chartInstances[id]; }
    };

    const gEl = document.getElementById("pd-gender");
    if (gEl && m.total > 0) {
      destroy("gender");
      chartInstances["gender"] = new Chart(gEl, {
        type: "doughnut",
        data: {
          labels: m.genderEntries.map(([k]) => k),
          datasets: [{
            data: m.genderEntries.map(([, v]) => v),
            backgroundColor: [C.blue, C.pink, C.amber],
            borderWidth: 2,
            borderColor: "#fff",
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: { font: { size: 10 }, padding: 6, boxWidth: 10 },
            },
          },
          cutout: "55%",
          onClick: (evt, elements) => {
            if (!elements.length) return;
            const idx = elements[0].index;
            const label = mRef.current.genderEntries[idx][0];
            if (label === "Male")
              openPatientDlgRef.current("Male Patients", mRef.current.maleP, "male_patients.csv");
            if (label === "Female")
              openPatientDlgRef.current("Female Patients", mRef.current.femaleP, "female_patients.csv");
            if (label === "Other")
              openPatientDlgRef.current("Other Gender Patients", mRef.current.otherP, "other_patients.csv");
          },
        },
      });
    }

    return () => { destroy("gender"); };
  }, [m.genderEntries, m.total, loading]);

  const locationColors = [C.teal, C.purple, C.blue, C.coral, C.amber, C.pink];
  const currentLocationData   = locationView === "city" ? m.cityData : m.stateData;
  const currentTableRows      = locationView === "city" ? m.cityTableRows : m.stateTableRows;
  const currentLocationCsv    = locationView === "city" ? "city_patients.csv" : "state_patients.csv";
  const currentViewAllTitle   = locationView === "city" ? "All Patients by City" : "All Patients by State";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxHeight: "calc(100vh - 100px)", overflowY: "auto", background: "#f7f8fa" }}>

      {/* ── MenuBar ── */}
      <Box sx={{ background: "#fff" }}>
        <MenuBar
          showRefresh={true}
          onRefresh={fetchPatients}
          refreshDisabled={loading}
          disabled={loading}
        />
        <Box sx={{ px: 1, pb: 0.75 }}>
          <Typography variant="body2" color="text.secondary">
            Showing recent patients (up to 100)
          </Typography>
        </Box>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ px: 2, py: 1.5, position: "relative" }}>

        {/* Loading overlay */}
        {loading && (
          <Box
            sx={{
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(255,255,255,0.6)", zIndex: 10,
            }}
          >
            <CircularProgress size={36} sx={{ color: C.teal }} />
          </Box>
        )}

        {/* ── Row 1: Stat Cards ── */}
        <Grid2 container spacing={1.5} sx={{ mb: 2 }}>

  <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
    <StatCard
      icon={<PeopleOutline />}
      label="Total Patients"
      value={m.total}
      color={C.teal}
      bgColor={C.tealL}
      onView={() => openPatientDlg("All Patients", patients, "all_patients.csv")}
    />
  </Grid2>

  <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
    <StatCard
      icon={<PersonAdd />}
      label="Newly Added"
      value={m.newlyAdded.length}
      color={C.blue}
      bgColor={C.blueL}
      subtitle="Last 30 days"
      onView={() => openPatientDlg("Newly Added Patients", m.newlyAdded, "new_patients.csv")}
    />
  </Grid2>

  {/* Gender pie chart — same row mein */}
  <Grid2 size={{ xs: 12, sm: 4, md: 4}}>
    <SectionCard
      title="Gender Split (click to view)"
      icon={<PeopleOutline />}
      color={C.purple}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ flex: 1, cursor: "pointer" }}>
          <ChartBox id="pd-gender" height={100} label="Gender distribution doughnut — click to view" />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{ textAlign: "center", cursor: "pointer", px: 1, py: 0.5, borderRadius: 1, "&:hover": { background: C.blueL } }}
            onClick={() => openPatientDlg("Male Patients", m.maleP, "male_patients.csv")}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: C.blue, lineHeight: 1 }}>{m.maleP.length}</Typography>
            <Typography variant="caption" color="text.secondary">Male</Typography>
          </Box>
          <Divider />
          <Box
            sx={{ textAlign: "center", cursor: "pointer", px: 1, py: 0.5, borderRadius: 1, "&:hover": { background: C.pinkL } }}
            onClick={() => openPatientDlg("Female Patients", m.femaleP, "female_patients.csv")}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: C.pink, lineHeight: 1 }}>{m.femaleP.length}</Typography>
            <Typography variant="caption" color="text.secondary">Female</Typography>
          </Box>
          {m.otherP.length > 0 && (
            <>
              <Divider />
              <Box
                sx={{ textAlign: "center", cursor: "pointer", px: 1, py: 0.5, borderRadius: 1, "&:hover": { background: C.amberL } }}
                onClick={() => openPatientDlg("Other Gender Patients", m.otherP, "other_patients.csv")}
              >
                <Typography variant="h6" fontWeight={700} sx={{ color: C.amber, lineHeight: 1 }}>{m.otherP.length}</Typography>
                <Typography variant="caption" color="text.secondary">Other</Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </SectionCard>
  </Grid2>

</Grid2>

        {/* ── Row 2: Age Distribution — traffic-colored ── */}
       <Grid2 container spacing={1.5} sx={{ mb: 2 }}>

  {/* Age Distribution — bada */}
  <Grid2 size={{ xs: 12, md: 10 }}>
    <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary"
              sx={{ textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}>
              Age Distribution
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#D32F2F" }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>High</Typography>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#F9A825", ml: 0.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>Medium</Typography>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", ml: 0.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>Low</Typography>
            </Box>
          </Box>
        }
        // action={
        //   <Typography variant="caption" color="text.secondary"
        //     sx={{ pr: 2, pt: 1, display: "block", fontSize: "0.65rem" }}>
        //     Click bar to view patients
        //   </Typography>
        // }
        sx={{ pb: 0, pt: 1.5, px: 2 }}
      />
     <CardContent sx={{ pt: 1, pb: "12px !important" }}>
  {Object.values(m.ageBuckets).every((v) => v === 0) ? (
    <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
      No age data available
    </Typography>
  ) : (
    <ChartBox
      id="pd-age"
      height={180}
      label="Age distribution bar chart"
      minWidth={Object.entries(m.ageBuckets).filter(([, v]) => v > 0).length * 60}
    />
  )}
</CardContent>
    </Card>
  </Grid2>

  {/* Avg Age — chota */}
  <Grid2 size={{ xs: 12, md: 2 }}>
    <StatCard
      icon={<Cake />}
      label="Avg Age"
      value={m.avgAge}
      color={C.amber}
      bgColor={C.amberL}
      subtitle="Years"
    />
  </Grid2>

</Grid2>
        

        {/* ── Row 3: Location Chart + Gender ── */}
        <Grid2 container spacing={1.5} sx={{ mb: 2 }}>

          {/* Combined City / State chart with dropdown */}
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocationOn sx={{ fontSize: 14, color: C.teal }} />
                    <Typography
                      variant="body2" fontWeight={600} color="text.secondary"
                      sx={{ textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: 0.5 }}
                    >
                      Patients by {locationView === "city" ? "City" : "State"}
                    </Typography>
                  </Box>
                }
                action={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1, pt: 0.5 }}>
                    {/* Dropdown — same style as WorklistDashboard groupBy */}
                    <select
                      value={locationView}
                      onChange={(e) => setLocationView(e.target.value)}
                      style={{
                        fontSize: "0.72rem",
                        border: "1px solid #e0e0e0",
                        borderRadius: 6,
                        padding: "3px 8px",
                        background: "#fff",
                        cursor: "pointer",
                        outline: "none",
                      }}
                    >
                      <option value="city">City</option>
                      <option value="state">State</option>
                    </select>
                    {/* View All button */}
                    <Button
                      size="small"
                      startIcon={<Visibility sx={{ fontSize: 12 }} />}
                      onClick={() =>
                        setDialog({
                          open: true,
                          title: currentViewAllTitle,
                          rows: currentTableRows,
                          columns: locationCols,
                          csv: currentLocationCsv,
                        })
                      }
                      sx={{ fontSize: "0.68rem", py: 0.2, color: C.teal }}
                    >
                      View All
                    </Button>
                  </Box>
                }
                sx={{ pb: 0, pt: 1.5, px: 2 }}
              />
              <CardContent sx={{ pt: 1, pb: "12px !important" }}>
                {currentLocationData.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                    No data
                  </Typography>
                ) : (
                  <>
                    {/* <ChartBox
                      id="pd-location"
                      height={Math.max(currentLocationData.length * 42 + 10, 110)}
                      label={`Horizontal bar: patients by ${locationView}`}
                    /> */}
                    {/* Mini leader bars below chart */}
                    <Box sx={{ mt: 1.5 }}>
                      {currentLocationData.map(([name, count], i) => (
                        <Box key={name} sx={{ mb: 1 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                            <Typography
                              variant="caption" fontWeight={i === 0 ? 600 : 400}
                              sx={{ maxWidth: "82%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {i + 1}. {name}
                            </Typography>
                            <Chip
                              label={count} size="small"
                              sx={{
                                height: 18, fontSize: "0.62rem",
                                background: locationColors[i % locationColors.length] + "22",
                                color: locationColors[i % locationColors.length], fontWeight: 700,
                              }}
                            />
                          </Box>
                          <Box sx={{ height: 5, background: "#ebebeb", borderRadius: 99 }}>
                            <Box
                              sx={{
                                width: `${Math.round((count / currentLocationData[0][1]) * 100)}%`,
                                height: "100%",
                                background: locationColors[i % locationColors.length],
                                borderRadius: 99, transition: "width 0.5s ease",
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid2>

          {/* Gender doughnut — clickable */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            {/* <SectionCard
              title="Gender Split (click to view)"
              icon={<PeopleOutline />}
              color={C.purple}
            >
              <Box sx={{ cursor: "pointer" }}>
                <ChartBox id="pd-gender" height={160} label="Gender distribution doughnut — click to view" />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-around", mt: 1 }}>
                <Box
                  sx={{
                    textAlign: "center", cursor: "pointer", px: 1, py: 0.5, borderRadius: 1,
                    "&:hover": { background: C.blueL },
                  }}
                  onClick={() => openPatientDlg("Male Patients", m.maleP, "male_patients.csv")}
                >
                  <Typography variant="h6" fontWeight={700} sx={{ color: C.blue, lineHeight: 1 }}>
                    {m.maleP.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Male</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box
                  sx={{
                    textAlign: "center", cursor: "pointer", px: 1, py: 0.5, borderRadius: 1,
                    "&:hover": { background: C.pinkL },
                  }}
                  onClick={() => openPatientDlg("Female Patients", m.femaleP, "female_patients.csv")}
                >
                  <Typography variant="h6" fontWeight={700} sx={{ color: C.pink, lineHeight: 1 }}>
                    {m.femaleP.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Female</Typography>
                </Box>
                {m.otherP.length > 0 && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Box
                      sx={{
                        textAlign: "center", cursor: "pointer", px: 1, py: 0.5, borderRadius: 1,
                        "&:hover": { background: C.amberL },
                      }}
                      onClick={() => openPatientDlg("Other Gender Patients", m.otherP, "other_patients.csv")}
                    >
                      <Typography variant="h6" fontWeight={700} sx={{ color: C.amber, lineHeight: 1 }}>
                        {m.otherP.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Other</Typography>
                    </Box>
                  </>
                )}
              </Box>
            </SectionCard> */}
          </Grid2>

        </Grid2>

      </Box>

      {/* Table dialog — same as WorklistDashboard */}
      <TableDialog
        open={dialog.open}
        onClose={closeDialog}
        title={dialog.title}
        rows={dialog.rows}
        columns={dialog.columns}
        csvFilename={dialog.csv}
        auth={auth}
      />
    </Box>
  );
}